import React from "react";
import {
  GdprSccVendorReviewStatus,
  OrganizationDataRecipientDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UpdateVendorFn } from "../../../hooks/get-compliant/vendors/useUpdateVendor";
import {
  VendorFlagOption,
  VendorFlagWithDocumentation,
  VendorRadioOption,
} from "./VendorFlagWithDocumentation";
import { ApiRouteHelpers } from "../../../../common/root/apiRoutes";
import { DocumentationInputValue } from "./DocumentationInput";
import { Tooltip } from "@mui/material";
import { isAdNetwork } from "../../../util/vendors";

type Props = {
  organizationId: string;
  openSafeCountriesDialog: () => void;
  recipients: OrganizationDataRecipientDto[];
  updateVendor: UpdateVendorFn;
  refresh: () => void;
  onContactUpdate: (recipient, textValue) => Promise<void>;
  onFileDelete: (recipient: OrganizationDataRecipientDto, fileKey: string) => Promise<void>;
  onFileUpload: (
    recipient: OrganizationDataRecipientDto,
    fileName: string,
    file: File,
  ) => Promise<{ fileKey: string }>;
  onUrlChange: (recipient: OrganizationDataRecipientDto, url: string) => Promise<void>;
};

export const GDPRSccDocumentationContactLocator: React.FC<Props> = ({
  organizationId,
  openSafeCountriesDialog,
  recipients,
  updateVendor,
  refresh,
  onContactUpdate,
  onFileDelete,
  onFileUpload,
  onUrlChange,
}) => {
  const updateSelection = async (r: OrganizationDataRecipientDto, flag: VendorFlagOption) => {
    let setting: boolean | null = null;
    if (flag == "DOCS_PROVIDED" && (r.gdprSccUrl || r.gdprSccFileKey)) {
      setting = true;
    } else {
      setting = false;
    }
    if (flag == "VENDOR_CONTACTED" && (r.email || r.defaultEmail)) {
      onContactUpdate(r, r.email || r.defaultEmail);
    }
    await updateVendor({
      vendorId: r.vendorId,
      gdprHasSccSetting: setting,
      gdprSccVendorReviewStatus: flag as GdprSccVendorReviewStatus,
    });
    refresh();
  };

  const updateUrl = async (r: OrganizationDataRecipientDto, url: string) => {
    await updateVendor({ vendorId: r.vendorId, gdprSccUrl: url });
    refresh();
  };

  const updateFile = async (r: OrganizationDataRecipientDto, name: string, key: string) => {
    await updateVendor({
      vendorId: r.vendorId,
      gdprSccFileName: name,
      gdprSccFileKey: key,
    });
    refresh();
  };

  const updateDocument = async (
    r: OrganizationDataRecipientDto,
    newValue: DocumentationInputValue,
    prevValue: DocumentationInputValue,
  ) => {
    if (prevValue?.type == "file" && (newValue?.type != "file" || !newValue.file)) {
      await onFileDelete(r, prevValue.fileKey);
    }
    if (newValue?.type == "file" && newValue.file) {
      await onFileUpload(r, newValue.fileName, newValue.file);
    }
    if (newValue?.type == "url") {
      await onUrlChange(r, newValue.url);
    }
  };

  const options: VendorRadioOption<GdprSccVendorReviewStatus, GdprSccVendorReviewStatus>[] = [
    {
      value: "DOCS_PROVIDED",
      type: "document",
      label: <div className="text-nowrap">Add my own docs</div>,
      key: "DOCS_PROVIDED",
      createFileLink: (recipient) =>
        ApiRouteHelpers.storage.vendorAgreements.byType(organizationId, recipient.vendorId, "scc"),
    },
    {
      value: "VENDOR_CONTACTED",
      type: "text",
      label: "Contact the vendor",
      textLabel: "Vendor contact email",
      placeholder: "contact@vendor.com",
      key: "VENDOR_CONTACTED",
      getTextValue: (recipient) => {
        return recipient.email || recipient.defaultEmail || "";
      },
    },
    {
      value: "FULFILLMENT_ONLY",
      type: "normal",
      dynamic: true,
      disabled: isAdNetwork,
      disabledTooltip: "This option does not apply to this type of vendor.",
      label: (recipient) => {
        if (!recipient.isPotentiallyExceptedFromSccs) {
          return "Vendor is used for contract fulfillment only";
        }
        return (
          <div className="d-flex align-items-center">
            Vendor is used for contract fulfillment only{" "}
            <Tooltip title="This vendor is flagged by our team as not requiring documentation.">
              <img
                className="ml-xs"
                src="/assets/images/icon-lightbulb.svg"
                color="primary"
                width="28"
              />
            </Tooltip>
          </div>
        );
      },
      key: "FULFILLMENT_ONLY",
    },
    {
      value: "SAFE_COUNTRIES_ONLY",
      type: "normal",
      label: (
        <>
          This vendor processes data only in{" "}
          <span
            className="text-weight-medium cursor-pointer text-decoration-underline color-secondary-600"
            onClick={(e) => {
              openSafeCountriesDialog();
              e.preventDefault();
            }}
          >
            safe countries
          </span>
        </>
      ),
      key: "SAFE_COUNTRIES_ONLY",
    },
  ];

  return (
    <VendorFlagWithDocumentation
      recipients={recipients}
      options={options}
      onFlagChanged={updateSelection}
      onUrlChanged={updateUrl}
      onFileChanged={updateFile}
      onTextChanged={onContactUpdate}
      onTextCleared={(recipient) => recipient.defaultEmail ?? ""}
      onDocumentChanged={updateDocument}
      getFlag={(r) => r.gdprSccVendorReviewStatus}
      getUrl={(r) => r.gdprSccUrl}
      getFileName={(r) => r.gdprSccFileName}
      getFileKey={(r) => r.gdprSccFileKey}
    />
  );
};
